import mustache from "mustache";
import { TareaEjecucionResponse } from "../../../../modulos/rastro/response/TareaEjecucionResponse";
import { DiaTareaEjecucionesBucket } from "../../../../modulos/rastro/services/TareaEjecucionGetYearGroupListByFincaIdService";
import { formatDiaSemanaDia } from "../../../../utils/fechaUtils";
import { _2d, _miles } from "../../../../utils/numberUtils";
import {
  colorDeEjecucion,
  colorTextoContraste,
} from "../../../../utils/tareaEjecucionColorUtils";
import { BaseSecondSidebarItemRender } from "../../../base/sidebar/BaseSecondSidebarItemRender";
import {
  SidebarExpandedStatsRender,
} from "../../../implementations/sidebar/SidebarExpandedStatsRender";
import template from "./tareaEjecucionSidebarItemContentTemplate.html";

mustache.parse(template);

/** Placeholder cuando un valor numérico no aplica. */
const SIN_DATO = "N/A";

/** Item del sidebar de Tareas: un día, con sus ejecuciones de tarea como
 *  tarjetas dentro del contenido expandido (badge del GPS Tracker, tarea
 *  asignada o "Sin asignar", acciones pantalla completa/ocultar/editar/
 *  eliminar, rango horario y métricas). Al seleccionarse notifica el día
 *  completo (el mapa dibuja todas sus ejecuciones); el ojo de cada tarjeta
 *  prende/apaga esa ejecución puntual sin afectar las demás; los botones
 *  pantalla completa/editar/eliminar notifican al sidebar (que abre el modal
 *  correspondiente o confirma y elimina). */
export class TareaEjecucionSidebarItemRender extends BaseSecondSidebarItemRender {
  /**
   * @param _dia               Balde del día (fecha + ejecuciones de ese día).
   * @param _onDiaSelected     Notifica el día al seleccionarse (el mapa dibuja
   *                           todas sus ejecuciones).
   * @param _onToggleEjecucion Notifica que se prendió/apagó una ejecución
   *                           puntual (el mapa agrega/quita esa capa).
   * @param _onEditEjecucion   Notifica que se pidió editar una ejecución (el
   *                           sidebar abre el modal de edición).
   * @param _onDeleteEjecucion Notifica que se pidió eliminar una ejecución
   *                           (el sidebar confirma y elimina).
   * @param _onFullScreenEjecucion Notifica que se pidió ver una ejecución a
   *                           pantalla completa (el sidebar abre el modal).
   */
  constructor(
    private readonly _dia: DiaTareaEjecucionesBucket,
    private readonly _onDiaSelected: (dia: DiaTareaEjecucionesBucket) => void,
    private readonly _onToggleEjecucion: (
      ejecucionId: string,
      visible: boolean,
    ) => void,
    private readonly _onEditEjecucion: (
      ejecucion: TareaEjecucionResponse,
    ) => void,
    private readonly _onDeleteEjecucion: (
      ejecucion: TareaEjecucionResponse,
    ) => void,
    private readonly _onFullScreenEjecucion: (
      ejecucion: TareaEjecucionResponse,
    ) => void,
  ) {
    super();
  }

  /** Día de la semana + día del mes (p. ej. "Lunes 19"). */
  protected getLabel(): string {
    return formatDiaSemanaDia(this._dia.fecha);
  }

  /** Sin título propio: el label ("Lunes 19") ya alcanza. */
  protected getTitle(): string {
    return "";
  }

  /** Tarjetas de las ejecuciones de tarea del día. */
  protected getItemContent(): string {
    return mustache.render(template, {
      ejecuciones: this._dia.ejecuciones.map((ejecucion) => {
        const color = colorDeEjecucion(this._dia.ejecuciones, ejecucion.id);
        return {
          id: ejecucion.id,
          color,
          colorTexto: colorTextoContraste(color),
          gpsTrackerNumero: ejecucion.gpsTrackerNumero,
          tareaNombreTexto: ejecucion.tareaNombre ?? "Sin asignar",
          horaInicio: this._hora(ejecucion.fechaInicio),
          horaFin: this._hora(ejecucion.fechaFin),
          stats: new SidebarExpandedStatsRender([
            {
              value:
                ejecucion.distanciaKm != null
                  ? _2d(ejecucion.distanciaKm)
                  : SIN_DATO,
              label: "Distancia Km",
            },
            {
              value:
                ejecucion.velocidadPromedioKmH != null
                  ? _2d(ejecucion.velocidadPromedioKmH)
                  : SIN_DATO,
              label: "Velocidad Km/h",
            },
            {
              value:
                ejecucion.muestrasEjecucion != null
                  ? _miles(ejecucion.muestrasEjecucion)
                  : SIN_DATO,
              label: "Muestras",
            },
          ]).render(),
          detalles: ejecucion.detalles.map((detalle) => ({
            productoDescripcion: detalle.productoDescripcion,
            unidadMedida: detalle.unidadMedida,
            dosis: _2d(detalle.dosis),
          })),
        };
      }),
    });
  }

  /** Al seleccionarse el día: el mapa dibuja todas sus ejecuciones. */
  protected onSelected(): void {
    this._onDiaSelected(this._dia);
  }

  /** Cablea los botones pantalla completa, ocultar/mostrar, editar y eliminar
   *  de cada ejecución del día. */
  protected connectExpanded(): void {
    for (const ejecucion of this._dia.ejecuciones) {
      const fullBtn = document.getElementById(`te-full-${ejecucion.id}`);
      if (fullBtn) {
        const handler = () => this._onFullScreenEjecucion(ejecucion);
        this._fullHandlers.set(ejecucion.id, handler);
        fullBtn.addEventListener("click", handler);
      }

      const toggleBtn = document.getElementById(`te-toggle-${ejecucion.id}`);
      if (toggleBtn) {
        const handler = () => this._onToggleClick(ejecucion, toggleBtn);
        this._toggleHandlers.set(ejecucion.id, handler);
        toggleBtn.addEventListener("click", handler);
      }

      const editBtn = document.getElementById(`te-edit-${ejecucion.id}`);
      if (editBtn) {
        const handler = () => this._onEditEjecucion(ejecucion);
        this._editHandlers.set(ejecucion.id, handler);
        editBtn.addEventListener("click", handler);
      }

      const deleteBtn = document.getElementById(`te-delete-${ejecucion.id}`);
      if (deleteBtn) {
        const handler = () => this._onDeleteEjecucion(ejecucion);
        this._deleteHandlers.set(ejecucion.id, handler);
        deleteBtn.addEventListener("click", handler);
      }
    }
  }

  /** Libera los listeners de los botones pantalla completa, ocultar/mostrar,
   *  editar y eliminar. */
  protected disposeExpanded(): void {
    for (const ejecucion of this._dia.ejecuciones) {
      const fullBtn = document.getElementById(`te-full-${ejecucion.id}`);
      const fullHandler = this._fullHandlers.get(ejecucion.id);
      if (fullBtn && fullHandler) {
        fullBtn.removeEventListener("click", fullHandler);
      }

      const toggleBtn = document.getElementById(`te-toggle-${ejecucion.id}`);
      const toggleHandler = this._toggleHandlers.get(ejecucion.id);
      if (toggleBtn && toggleHandler) {
        toggleBtn.removeEventListener("click", toggleHandler);
      }

      const editBtn = document.getElementById(`te-edit-${ejecucion.id}`);
      const editHandler = this._editHandlers.get(ejecucion.id);
      if (editBtn && editHandler) {
        editBtn.removeEventListener("click", editHandler);
      }

      const deleteBtn = document.getElementById(`te-delete-${ejecucion.id}`);
      const deleteHandler = this._deleteHandlers.get(ejecucion.id);
      if (deleteBtn && deleteHandler) {
        deleteBtn.removeEventListener("click", deleteHandler);
      }
    }
    this._fullHandlers.clear();
    this._toggleHandlers.clear();
    this._editHandlers.clear();
    this._deleteHandlers.clear();
  }

  /** Ids de ejecuciones actualmente ocultas (para el ícono del botón). */
  private readonly _ocultas = new Set<string>();
  /** Handlers de click registrados por ejecución (para poder liberarlos). */
  private readonly _toggleHandlers = new Map<string, () => void>();
  /** Handlers de click del botón editar, por ejecución. */
  private readonly _editHandlers = new Map<string, () => void>();
  /** Handlers de click del botón eliminar, por ejecución. */
  private readonly _deleteHandlers = new Map<string, () => void>();
  /** Handlers de click del botón pantalla completa, por ejecución. */
  private readonly _fullHandlers = new Map<string, () => void>();

  /** Alterna el ícono según la acción disponible (no el estado actual):
   *  visible → ícono "ocultar" (ojo tachado); oculta → ícono "visualizar"
   *  (ojo). Notifica el nuevo estado de visibilidad. */
  private _onToggleClick(
    ejecucion: TareaEjecucionResponse,
    btn: HTMLElement,
  ): void {
    const oculta = this._ocultas.has(ejecucion.id);
    const icon = btn.querySelector("i");
    if (oculta) {
      this._ocultas.delete(ejecucion.id);
      icon?.classList.replace("ph-eye", "ph-eye-slash");
    } else {
      this._ocultas.add(ejecucion.id);
      icon?.classList.replace("ph-eye-slash", "ph-eye");
    }
    this._onToggleEjecucion(ejecucion.id, oculta);
  }

  /** Hora local (HH:mm) a partir de una fecha-hora ISO (UTC). */
  private _hora(fechaHoraIso: string): string {
    return new Date(fechaHoraIso).toLocaleTimeString("es-CO", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }
}
